import { useEffect, useMemo, useRef, useState } from 'react'

import styled from 'styled-components'

import BrandingUploader from 'app/agency/branding_uploader/component'
import {
  sendTestEmailWithBrandingSettings,
  updateAgencyConfig,
} from 'app/bond_cover/agency/agency_access'
import {
  Box,
  Button,
  Flex,
  Modal,
  SelectColor,
} from 'app/components/design-system-components'
import { Input, TextArea } from 'app/components/design-system-components/inputs'
import {
  Select,
  SimpleOptionContainer,
} from 'app/components/design-system-components/inputs/Select/Select'
import {
  fontSizes,
  fontWeight,
} from 'app/components/design-system-components/styles/variables'
import { ToolTip } from 'app/components/design-system-components/Tooltip'
import { Text } from 'app/components/design-system-components/typography'
import UpgradeCTA from 'app/components/design-system-components/UpgradeCTA'
import * as Display from 'app/components/display/display'
import { translateErrorCodeToMessage } from 'app/constants/error_messages'
import { theme } from 'app/match/applicationReportPDF/assets/theme'
import { getAgencySettings } from 'app/services/http/agency'
import { getAgencyProfileAndDetails } from 'app/services/http/agency'
import * as snugNotifier from 'app/services/snugNotifier'
import UnsavedMessage from 'app/shared_components/unsaved_message'
import * as validations from 'app/shared_components/validations'
import { AgencyBrandingType } from 'app/sm/helpers'
import { brandingTestEmailTemplatesOptions } from 'app/sm/team_viewing_preference/branding/constants'
import UserNotAuthorizedShowTeamMembersList from 'app/sm/team_viewing_preference/userNotAuthorized/showTeamMembersList'

const BrandingColourOptionElem = styled(SimpleOptionContainer)`
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
`

type StyledColorBoxProps = {
  bgColor: string
}

const StyledColorBox = styled.div<StyledColorBoxProps>`
  background-color: ${({ bgColor }) => bgColor};
  height: 16px;
  width: 16px;
  cursor: pointer;
  border-radius: 4px;
  border: solid 1px #cccccc;
`

type DropdownOption = {
  value: string
  optionMeta: {
    id: number
    displayText: string
  }
}

const StyledResetButton = styled(Button)`
  margin-left: ${theme.baseSpace}px;
  font-size: ${fontSizes[4]}px !important;
  font-weight: ${fontWeight[3]} !important;
  margin-top: -${theme.space[1]}px;
`

const ResetButton = ({ onClickHandler }: { onClickHandler: () => void }) => {
  return (
    <StyledResetButton
      variant="linkBlueWithoutWeight"
      onClick={() => onClickHandler()}
    >
      reset
    </StyledResetButton>
  )
}

const SettingSectionContainer = styled(Box)`
  margin-bottom: ${theme.space[17]}px;
`

const defaultSnugColours = {
  primary: '#1ab25d',
  highlight: '#ff5b77',
}

export const defaultBrandColorOptions: DropdownOption[] = Object.entries(
  defaultSnugColours,
).map(([_, displayText], idx) => ({
  value: displayText,
  optionMeta: {
    id: idx,
    displayText,
  },
}))

export const signatureOptions: DropdownOption[] = [
  {
    value: 'agency_sign_off',
    optionMeta: {
      id: 1,
      displayText: 'Agency sign off',
    },
  },
  {
    value: 'listing_agent_sign_off',
    optionMeta: {
      id: 2,
      displayText: 'Listing agent sign off',
    },
  },
]

export const emailPreviewOptions: DropdownOption[] = Object.entries(
  brandingTestEmailTemplatesOptions,
).map(([id, displayText], idx) => ({
  value: id,
  optionMeta: {
    id: idx,
    displayText,
  },
}))

const SelectColorOptionCmp = ({
  option,
  onClick,
  isLast,
  selected,
  ...props
}: any) => {
  return (
    <BrandingColourOptionElem
      onClick={onClick}
      className={selected && 'selected'}
      {...props}
    >
      <StyledColorBox bgColor={option.value} />
    </BrandingColourOptionElem>
  )
}

const SelectBrandingColorComponent = ({
  label,
  name,
  options,
  value,
  onChangeInputValue,
  disabled,
}: any) => {
  return (
    <Box mr={3} width={'100px'}>
      <Text as="h3" fontSize={fontSizes[5] + 'px'} mb={3}>
        {label}
      </Text>
      <Select
        options={options}
        value={value}
        changeHandler={(opt: any) => onChangeInputValue(name)(opt)}
        label={<br />}
        OptionsHeaderRenderCmp={null}
        width="56px"
        triggerWrapperComponentProps={{
          height: '32px',
          padding: '0px 8px !important',
          disabled: disabled,
        }}
        triggerIconContainerProps={{ ml: '-8px !important' }}
        OptionRenderCmp={SelectColorOptionCmp}
        onlyTriggerOnChange
        TriggerWrapperCmp={undefined}
        disabled={disabled}
        blurHandler={undefined}
        touched={undefined}
        error={undefined}
        resultsLabel={undefined}
        NoOptionsCmp={undefined}
        buttonVariant={undefined}
      />
    </Box>
  )
}

type IBrandingProps = {
  teamGUID: any
  isAdmin: any
  teamSlug: any
  updateTeamViewingSettingInfo: any
  removeAgencyBranding: any
  addAgencyBranding: any
  onBackButtonClicked: any
  fetchAgencyInfo: any
  agency: any
  currentUserEmail: any
  isActionDisabled: boolean
  isPremierAccount: boolean
}

function Branding({
  teamGUID,
  isAdmin,
  teamSlug,
  updateTeamViewingSettingInfo,
  removeAgencyBranding,
  addAgencyBranding,
  onBackButtonClicked,
  fetchAgencyInfo,
  agency,
  currentUserEmail,
  isActionDisabled,
  isPremierAccount,
}: IBrandingProps) {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSendTestEmailInProgress, setIsSendTestEmailInProgressing] =
    useState(false)
  const [showSendTestEmailModal, setShowSendTestEmailModal] = useState(false)
  const [emails, setEmails] = useState(currentUserEmail)
  const [validationEmailsErrors, setValidationEmailsErrors] = useState('')
  const [unsavedChanges, setUnsavedChanges] = useState(false)
  const [showConfirmRemoveLogoModal, setShowConfirmRemoveLogoModal] =
    useState(false)
  const [
    showConfirmResetDefaultColorModal,
    setShowConfirmResetDefaultColorModal,
  ] = useState(false)

  const [colorUnsavedChanges, setColorUnsavedChanges] = useState(false)
  const [isColoursHovered, setIsColoursHovered] = useState(false)

  const [formDetails, setFormDetails] = useState({
    brandingOfficeName: '',
    brandingAbout: '',
    brandingDisclaimer: '',
    signatureOption: signatureOptions[0],
    emailPreviewOption: emailPreviewOptions[0],
    primaryColorOption: defaultBrandColorOptions[0],
    highlightColorOption: defaultBrandColorOptions[1],
  })

  const changeInputValue = (field: any) => {
    if (field !== 'emailPreviewOption') {
      setUnsavedChanges(true)
    }

    return (data: any) => {
      setFormDetails({
        ...formDetails,
        [field]: data,
      })
    }
  }

  const [logoUploadError, setLogoUploadError] = useState('')
  const [brandingLogoURL, setBrandingLogoURL] = useState('')

  const [brandColourOptions, setBrandColourOptions] = useState<
    DropdownOption[]
  >([])
  const displayBrandColourOptions = useMemo(() => {
    const newBrandColourOptions = !isActionDisabled
      ? [
          ...brandColourOptions,
          {
            value: '',
            optionMeta: { id: -1, displayText: 'Create New' },
          },
        ]
      : brandColourOptions
    return newBrandColourOptions
  }, [brandColourOptions, isActionDisabled])

  const getDropdownOption = (
    dropdownOptions: DropdownOption[],
    value?: string,
  ) => {
    return dropdownOptions?.find((o) => o.value === value)
  }

  useEffect(() => {
    fetchAgencyInfo(teamGUID)
    teamGUID &&
      getAgencyProfileAndDetails(teamGUID)
        .then((data: any) => {
          const { brandingLogoURL } = data
          setBrandingLogoURL(brandingLogoURL)
        })
        .catch((err: any) => {
          const errorMessage = err?.message || 'Failed to load agency profile'
          snugNotifier.error(errorMessage)
        })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (agency.guidID) {
      getAgencySettings(agency.guidID, 'branding')
        .then((data: any) => {
          const { branding } = data.settingInfo

          const {
            branding_colours,
            branding_primary_colour,
            branding_highlight_colour,
            branding_about,
            branding_disclaimer,
            branding_signature,
          } = branding
          const brandingColoursOptions = branding_colours
            ? branding_colours.split(',').map((bc: string, idx: number) => ({
                value: bc.toLowerCase(),
                optionMeta: {
                  id: idx,
                  displayText: bc,
                },
              }))
            : defaultBrandColorOptions

          const primaryColorOption =
            getDropdownOption(
              brandingColoursOptions,
              branding_primary_colour,
            ) ?? defaultBrandColorOptions[0]

          const highlightColorOption =
            getDropdownOption(
              brandingColoursOptions,
              branding_highlight_colour,
            ) ?? defaultBrandColorOptions[1]

          const signatureOption =
            getDropdownOption(signatureOptions, branding_signature) ??
            signatureOptions[0]

          setBrandColourOptions(brandingColoursOptions)

          setFormDetails({
            ...formDetails,
            brandingOfficeName: agency.name,
            brandingAbout: branding_about,
            brandingDisclaimer: branding_disclaimer,
            primaryColorOption,
            highlightColorOption,
            signatureOption,
          })
        })
        .catch((err: any) => {
          const errorMessage =
            err?.message || 'Failed to load branding settings'
          snugNotifier.error(errorMessage)
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agency.guidID])

  useEffect(() => {
    if (!brandColourOptions.length) return

    const { primaryColorOption, highlightColorOption } = formDetails
    const { primary, highlight } = defaultSnugColours

    const hasUnsavedChanges =
      primaryColorOption?.value !== primary ||
      highlightColorOption?.value !== highlight ||
      brandColourOptions.length !== defaultBrandColorOptions.length ||
      brandColourOptions[0]?.value !== primary ||
      brandColourOptions[1]?.value !== highlight

    if (hasUnsavedChanges) {
      setColorUnsavedChanges(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formDetails.primaryColorOption,
    formDetails.highlightColorOption,
    brandColourOptions,
  ])

  const onRemoveAgencyBranding = () => {
    removeAgencyBranding(AgencyBrandingType.Logo, teamGUID).then(() => {
      setBrandingLogoURL('')
      setLogoUploadError('')
      toggleConfirmRemoveLogoModal()
      setUnsavedChanges(true)
    })
  }

  const onUploadBranding = (
    logoType: string,
    agencyGUID: string,
    documentType: string,
  ) => {
    addAgencyBranding(logoType, agencyGUID, documentType)
      .then((brandingURL: string) => {
        setBrandingLogoURL(brandingURL)
        setLogoUploadError('')
        setUnsavedChanges(true)
      })
      .catch((error: any) => {
        setLogoUploadError(error)
      })
  }

  const checkIfColorValid = (color: string) => {
    return !brandColourOptions
      .map((option) => option.value?.toLowerCase())
      .includes(color)
  }

  const onChangeColorPicker = (
    optionValue: string,
    { value, setColorValue }: any,
  ) => {
    if (!checkIfColorValid(value.hex)) {
      return snugNotifier.error('This colour is already in the set!')
    }

    setBrandColourOptions((prev) => {
      const updatedOptions = prev.map((option) =>
        option.value === optionValue ? { ...option, value: value.hex } : option,
      )

      let primaryColor = formDetails.primaryColorOption?.value
      let highlightColor = formDetails.highlightColorOption?.value

      if (optionValue.toLowerCase() === primaryColor?.toLowerCase()) {
        primaryColor = value.hex
      }

      if (optionValue.toLowerCase() === highlightColor?.toLowerCase()) {
        highlightColor = value.hex
      }

      setFormDetails({
        ...formDetails,
        primaryColorOption: getDropdownOption(updatedOptions, primaryColor),
        highlightColorOption: getDropdownOption(updatedOptions, highlightColor),
      })

      setUnsavedChanges(true)
      setColorValue(value)
      return updatedOptions
    })
  }

  const onAddColour = ({ value, handleClose }: any) => {
    if (!checkIfColorValid(value.hex)) {
      return snugNotifier.error('This colour is already in the set!')
    }

    setBrandColourOptions((prev) => {
      const newOption = {
        value: value.hex,
        optionMeta: { id: prev.length + 1, displayText: '' },
      }

      const updatedOptions = [...prev]
      updatedOptions.splice(Math.max(prev.length, 0), 0, newOption)

      setUnsavedChanges(true)
      handleClose()
      return updatedOptions
    })
  }

  const onDeleteColour = (optionValue: string) => {
    if (brandColourOptions.length <= 2) {
      return snugNotifier.error('Please have at least 2 brand colours')
    }

    const isColorInUse = [
      formDetails.primaryColorOption,
      formDetails.highlightColorOption,
    ].some((option) => option?.value === optionValue)

    if (isColorInUse) {
      return snugNotifier.error(
        'This colour is currently in use. Please change your settings before deleting.',
      )
    }

    setBrandColourOptions((prev) => {
      const newBrandColourOptions = prev.filter(
        (option) => option.value !== optionValue,
      )

      setFormDetails((prevDetails) => ({
        ...prevDetails,
        primaryColorOption:
          getDropdownOption(
            newBrandColourOptions,
            prevDetails.primaryColorOption?.value,
          ) ?? newBrandColourOptions[0],
        highlightColorOption:
          getDropdownOption(
            newBrandColourOptions,
            prevDetails.highlightColorOption?.value,
          ) ?? newBrandColourOptions[1],
      }))

      setUnsavedChanges(true)
      return newBrandColourOptions
    })
  }

  const toggleConfirmResetDefaultColorModal = () => {
    setShowConfirmResetDefaultColorModal(!showConfirmResetDefaultColorModal)
  }

  const resetBrandingColourSettings = () => {
    setFormDetails({
      ...formDetails,
      primaryColorOption: defaultBrandColorOptions[0],
      highlightColorOption: defaultBrandColorOptions[1],
    })
    setUnsavedChanges(true)
    setBrandColourOptions(defaultBrandColorOptions)
    setColorUnsavedChanges(false)
    toggleConfirmResetDefaultColorModal()
  }

  const onConfirmButtonClicked = () => {
    setIsLoading(true)
    let brandingSettings: any
    brandingSettings = {
      branding_colours: brandColourOptions.map((o) => o.value).join(','),
      branding_primary_colour: formDetails.primaryColorOption?.value,
      branding_highlight_colour: formDetails.highlightColorOption?.value,
      branding_about: formDetails.brandingAbout,
      branding_disclaimer: formDetails.brandingDisclaimer,
      branding_signature: formDetails.signatureOption?.value,
    }

    const { agencyAddress, guidID, ...agencySettings } = agency
    updateAgencyConfig(teamGUID, {
      ...agencySettings,
      address: agencyAddress,
      agencyGUID: teamGUID,
      name: formDetails.brandingOfficeName,
    })
      .then(() => {
        updateTeamViewingSettingInfo(
          teamGUID,
          {},
          {},
          {},
          {},
          {},
          {},
          brandingSettings,
        ).then(() => {
          snugNotifier.success('Settings have been saved successfully.')
          setIsLoading(false)
          setUnsavedChanges(false)
          wrapperRef?.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          })
          return Promise.resolve()
        })
      })
      .catch((error: any) => {
        setIsLoading(false)
        const errorMessage = error.message || 'Failed to save settings'
        snugNotifier.error(errorMessage)
        return Promise.reject()
      })
  }

  const toggleSendTestEmailModal = () => {
    setEmails(currentUserEmail)
    setShowSendTestEmailModal((prev) => !prev)
  }

  const toggleConfirmRemoveLogoModal = () => {
    setShowConfirmRemoveLogoModal(!showConfirmRemoveLogoModal)
  }

  const validateEmails = (event: any) => {
    const emailsList = event?.target?.value
      ? event?.target?.value?.split(',')
      : []

    if (emailsList.length > 3) {
      return setValidationEmailsErrors(
        'Please enter valid email, comma separated, maximum 3',
      )
    }

    emailsList.forEach((email: string) => {
      const error = validations.validateEmail(
        email.trim(),
        'Invalid email address',
      )

      if (error && error.length > 0) {
        return setValidationEmailsErrors(
          'Please enter valid email, comma separated, maximum 3',
        )
      } else {
        setValidationEmailsErrors('')
      }
    })
  }

  const handleSendTestEmail = () => {
    if (validationEmailsErrors !== '') return
    const brandingSettings = {
      primary_colour: formDetails.primaryColorOption?.value,
      highlight_colour: formDetails.highlightColorOption?.value,
      branding_about: formDetails.brandingAbout,
      branding_disclaimer: formDetails.brandingDisclaimer,
      branding_office_name: formDetails.brandingOfficeName,
      branding_signature: formDetails.signatureOption?.value,
      to: emails,
      branding_test_email_template: formDetails.emailPreviewOption?.value,
    }

    setIsSendTestEmailInProgressing(true)
    sendTestEmailWithBrandingSettings(teamGUID, brandingSettings)
      .then(() => {
        snugNotifier.success('Sent test email successfully.')
        toggleSendTestEmailModal()
      })
      .catch((err: any) => {
        snugNotifier.error(translateErrorCodeToMessage(err.errorCode))
      })
      .finally(() => setIsSendTestEmailInProgressing(false))
  }

  return (
    <Box ref={wrapperRef}>
      <Text as="h1" mb={4} mt={0} fontSize={fontSizes[12] + 'px'}>
        Branding
      </Text>
      <Box mb={8} fontSize={5}>
        Set your branding assets for email communication{' '}
        <a
          href="https://help.snug.com/hc/en-us/articles/360001057435-Agency-Branding-How-to-Set-up-your-Logo-Banner-and-SMS-name"
          target="_blank"
          rel="noreferrer"
        >
          learn more
        </a>
      </Box>

      {!isPremierAccount && (
        <SettingSectionContainer>
          <UpgradeCTA
            alignRight={false}
            availableText="This feature is available on"
            availableOn="Premier plans"
            maxWidth="100%"
            textContainerProps={{
              display: 'flex',
              alignItems: 'center',
              lineHeight: '1.3',
            }}
            learnmoreVariant="linkBlueWithoutWeight"
          />
        </SettingSectionContainer>
      )}

      {isPremierAccount && !isAdmin && (
        <SettingSectionContainer>
          <UserNotAuthorizedShowTeamMembersList teamSlug={teamSlug} />
        </SettingSectionContainer>
      )}

      <SettingSectionContainer>
        <Text as="h3" fontSize={fontSizes[7] + 'px'} mb={2}>
          Office name
        </Text>
        <Text mt={0} mb={theme.space[5] + 'px'}>
          Used as the sender name.
        </Text>
        <Input
          inputProps={{
            placeholder: 'Office name',
            value: formDetails.brandingOfficeName,
            onChange: (event: any) =>
              changeInputValue('brandingOfficeName')(event.target.value),
          }}
          width="200px"
          height="44px"
          containerProps={{
            width: '200px',
          }}
          id={undefined}
          icon={undefined}
          prefix={undefined}
          postfix={undefined}
          endedIcon={undefined}
          disabled={isActionDisabled}
        />
      </SettingSectionContainer>
      <SettingSectionContainer>
        <Text as="h3" fontSize={fontSizes[7] + 'px'} mb={3}>
          Logos
        </Text>
        <Text mt={0} mb={5}>
          Select logos to include in emails. 200x50px required.
        </Text>
        <Box mb={`-${theme.space[1]}px`}>
          <BrandingUploader
            header="Logo"
            logoType={AgencyBrandingType.Logo}
            brandingURL={brandingLogoURL}
            agencyGUID={teamGUID}
            error={logoUploadError}
            hideHeader
            onUploadBranding={onUploadBranding}
            onRemoveBranding={toggleConfirmRemoveLogoModal}
            brandingImgClass="logo-size"
            brandingRequirement=""
            validationError={''}
            disabled={isActionDisabled}
          />
        </Box>
      </SettingSectionContainer>
      <SettingSectionContainer
        onMouseEnter={() => setIsColoursHovered(true)}
        onMouseLeave={() => setIsColoursHovered(false)}
      >
        <Flex>
          <Text as="h3" fontSize={fontSizes[7] + 'px'} mb={3}>
            Colours
          </Text>
          {colorUnsavedChanges && isColoursHovered && (
            <ResetButton onClickHandler={toggleConfirmResetDefaultColorModal} />
          )}
        </Flex>
        <Text mt={0} mb={0}>
          Set colours for various email elements.
        </Text>
        <Box mt={5}>
          <Text as="h3" fontSize={fontSizes[5] + 'px'} mb={3}>
            Brand colours
          </Text>
          <Flex>
            {displayBrandColourOptions?.map((o) => {
              return (
                <Box key={o.value ?? 'add_new_colour'} mr={4}>
                  <SelectColor
                    defaultValue={o.value as any}
                    onChangeCustom={(onChangeEvent: any) => {
                      o.value
                        ? onChangeColorPicker(o.value, onChangeEvent)
                        : onAddColour(onChangeEvent)
                    }}
                    onDelete={() => {
                      onDeleteColour(o.value)
                    }}
                    disabled={isActionDisabled}
                  />
                </Box>
              )
            })}
          </Flex>
        </Box>
        <Box mt={5}>
          <Flex flexDirection={'row'}>
            <SelectBrandingColorComponent
              label={'Primary'}
              name={'primaryColorOption'}
              value={formDetails.primaryColorOption}
              options={brandColourOptions}
              onChangeInputValue={changeInputValue}
              disabled={isActionDisabled}
            />
            <SelectBrandingColorComponent
              label={'Highlight'}
              name={'highlightColorOption'}
              value={formDetails.highlightColorOption}
              options={brandColourOptions}
              onChangeInputValue={changeInputValue}
              disabled={isActionDisabled}
            />
          </Flex>
        </Box>
      </SettingSectionContainer>
      <SettingSectionContainer>
        <Text as="h3" fontSize={fontSizes[7] + 'px'} mb={3}>
          About
        </Text>
        <Text mb={theme.space[5] + 'px'}>
          Set agency details to include in emails.
        </Text>
        <TextArea
          placeholder="Enter Description"
          value={formDetails.brandingAbout}
          onChange={(event: any) =>
            changeInputValue('brandingAbout')(event.target.value)
          }
          rows={5}
          maxLength={200}
          resizedQuerySelectorId={'resized-textarea' as any}
          containerProps={{
            className: `${isActionDisabled && 'disabled'}`,
          }}
          disabled={isActionDisabled}
        />
      </SettingSectionContainer>
      <SettingSectionContainer>
        <Text as="h3" fontSize={fontSizes[7] + 'px'} mb={3}>
          Disclaimer
        </Text>
        <Text mb={theme.space[5] + 'px'}>
          Add a disclaimer that will be included in all branded emails.
        </Text>
        <TextArea
          placeholder="Enter Description"
          value={formDetails.brandingDisclaimer}
          onChange={(event: any) =>
            changeInputValue('brandingDisclaimer')(event.target.value)
          }
          rows={5}
          maxLength={200}
          resizedQuerySelectorId={'resized-textarea' as any}
          containerProps={{
            className: `${isActionDisabled && 'disabled'}`,
          }}
          disabled={isActionDisabled}
        />
      </SettingSectionContainer>
      <SettingSectionContainer>
        <Text as="h3" fontSize={fontSizes[7] + 'px'} mb={3}>
          Signature
        </Text>
        <Flex alignItems="center" mb={theme.space[5] + 'px'}>
          <Text as="span" mb={0} mr={3}>
            Set the type of signature used in agency emails.
          </Text>
          <ToolTip
            toolTipID="signatureTooltipID"
            content={
              <span>
                Sign off with the Agency phone number and either the Agency or
                Listing agent name.
              </span>
            }
          />
        </Flex>
        <Select
          options={signatureOptions}
          value={formDetails.signatureOption}
          changeHandler={(opt: DropdownOption) =>
            changeInputValue('signatureOption')(opt)
          }
          label="Selected signature"
          OptionsHeaderRenderCmp={null}
          width="240px"
          triggerWrapperComponentProps={{
            height: '40px',
            disabled: isActionDisabled,
          }}
          mr={4}
          OptionRenderCmp={undefined}
          TriggerWrapperCmp={undefined}
          disabled={isActionDisabled}
          blurHandler={undefined}
          touched={undefined}
          error={undefined}
          resultsLabel={undefined}
          NoOptionsCmp={undefined}
          buttonVariant={undefined}
          triggerIconContainerProps={undefined}
        />
      </SettingSectionContainer>
      <SettingSectionContainer>
        <Text as="h3" fontSize={fontSizes[7] + 'px'} mb={3}>
          Preview
        </Text>
        <Text mb={theme.space[5] + 'px'} mt={0}>
          Preview the selected branded email by sending a test.
        </Text>
        <Flex>
          <Select
            options={emailPreviewOptions}
            value={formDetails.emailPreviewOption}
            changeHandler={(opt: DropdownOption) =>
              changeInputValue('emailPreviewOption')(opt)
            }
            label="Selected email"
            OptionsHeaderRenderCmp={null}
            width="240px"
            triggerWrapperComponentProps={{ height: '40px' }}
            mr={5}
            OptionRenderCmp={undefined}
            TriggerWrapperCmp={undefined}
            disabled={undefined}
            blurHandler={undefined}
            touched={undefined}
            error={undefined}
            resultsLabel={undefined}
            NoOptionsCmp={undefined}
            buttonVariant={undefined}
            triggerIconContainerProps={undefined}
          />
          <Button
            onClick={toggleSendTestEmailModal}
            variant="brightGreenOutline"
          >
            Send test
          </Button>
        </Flex>
      </SettingSectionContainer>

      {!isActionDisabled && (
        <Display.ModalButtonGroup
          primaryLabel="Save"
          secondaryLabel="Cancel"
          secondaryButtonType="tertiary"
          justifyContent="flex-start"
          primaryAction={onConfirmButtonClicked}
          isLoading={isLoading}
          secondaryAction={onBackButtonClicked}
        />
      )}

      {showSendTestEmailModal && (
        <Modal
          modalHeading="Send test email"
          modalBody={
            <Box
              style={{ lineHeight: theme.lineHeights.encrption, width: '100%' }}
              mt={3}
            >
              <Text mb={7}>
                Send a test email to the comma separated recipients. If property
                details are required, a random property will be selected from
                available properties.
              </Text>
              <Text mb={5} fontWeight={theme.fontWeights.bold}>
                Recipient(s)
              </Text>
              <strong></strong>
              <Input
                inputProps={{
                  placeholder: 'Enter email',
                  value: emails,
                  onChange: (event: any) => setEmails(event?.target?.value),
                }}
                height="44px"
                onBlur={validateEmails}
                error={validationEmailsErrors as any}
                id={undefined}
                icon={undefined}
                prefix={undefined}
                postfix={undefined}
                endedIcon={undefined}
                containerProps={undefined}
              />
              {validationEmailsErrors !== '' && (
                <error>{validationEmailsErrors}</error>
              )}
            </Box>
          }
          primaryLabel="Send"
          secondaryLabel="Cancel"
          primaryAction={handleSendTestEmail}
          secondaryAction={toggleSendTestEmailModal}
          primaryButtonLoading={isSendTestEmailInProgress}
          toggleModal={toggleSendTestEmailModal}
          modalFooter={undefined}
        />
      )}

      {showConfirmRemoveLogoModal && (
        <Modal
          modalHeading="Are you sure?"
          modalBody={
            <Box
              style={{ lineHeight: theme.lineHeights.encrption, width: '100%' }}
              mt={3}
            >
              Are you sure you would like to remove the branding logo?
            </Box>
          }
          primaryLabel="Remove"
          secondaryLabel="Cancel"
          primaryAction={onRemoveAgencyBranding}
          secondaryAction={toggleConfirmRemoveLogoModal}
          toggleModal={toggleConfirmRemoveLogoModal}
          modalFooter={undefined}
        />
      )}

      {showConfirmResetDefaultColorModal && (
        <Modal
          modalHeading="Are you sure?"
          modalBody={
            <Box
              style={{ lineHeight: theme.lineHeights.encrption, width: '100%' }}
              mt={3}
            >
              This will reset to the default Brand colours and Primary and
              Highlight selections. Would you like to continue?
            </Box>
          }
          primaryLabel="Confirm"
          secondaryLabel="Cancel"
          primaryAction={resetBrandingColourSettings}
          secondaryAction={toggleConfirmResetDefaultColorModal}
          toggleModal={toggleConfirmResetDefaultColorModal}
          modalFooter={undefined}
        />
      )}

      <UnsavedMessage unsavedChanges={unsavedChanges} message={undefined} />
    </Box>
  )
}

export default Branding
